import { formatEur } from "@gladis/shared";
import { Button, Dialog } from "@gladis/ui";
import { useT } from "@gladis/ui/i18n";
import type { ReactNode } from "react";

import type { IncomeEntryDto } from "../dto.js";

/**
 * Deleting a one-off receipt.
 *
 * Simpler than a source's two-way choice, because a one-off has no lifecycle to confuse it with: it
 * either arrived or it did not. The figure and the date are repeated in the question, since "delete
 * this?" beside a list of six similar-looking bonuses is not enough to be sure which one is meant.
 *
 * Soft-deleted underneath, so the household's audit trail keeps it — but the dialog does not say so,
 * because "deleted but not really" is a promise about implementation that the interface should not
 * be making.
 */
export interface DeleteEntryDialogProps {
  open: boolean;
  onClose: () => void;
  entry: IncomeEntryDto;
  onConfirm: () => void;
  pending: boolean;
  error?: string | null;
}

export function DeleteEntryDialog({
  open,
  onClose,
  entry,
  onConfirm,
  pending,
  error,
}: DeleteEntryDialogProps): ReactNode {
  const t = useT();
  return (
    <Dialog open={open} onClose={onClose} title={`Delete ${entry.description}?`}>
      <div className="space-y-4" role="alert">
        <p>
          {formatEur(entry.amountInEuros)} on {entry.receivedOn}. It will stop counting towards that
          month and that year.
        </p>

        {error ? (
          <p className="text-sm text-danger" role="alert">
            {error}
          </p>
        ) : null}

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button type="button" variant="primary" onClick={onConfirm} disabled={pending}>
            {pending ? t("common.deleting") : t("inc.entry.deleteConfirm")}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
